
import { useCallback, useEffect, useRef, useState } from "react";
import Sidebar from "./Sidebar";
import { Outlet, useLocation } from "react-router-dom";
import TopBar from "./TopBar";
import ProcessingQueue from "../users/ProcessingQueue";
import api from "../api"; // adjust path if needed
import { useSelector } from "react-redux";
import FilePreviewPanel from "./FilePreviewPanel";
import "../css/FilePreviewPanel.css";

const DEFAULT_AI_PREFERENCES = {
  //LENGTH_OPTIONS
  tone: "Default",
  length: "long",
  aiRole: "Default",
  userRole: "Default",
};

const MainLayout1 = () => {
  const token = localStorage.getItem("access_token");
  const location = useLocation();
  const activeSessionId = useSelector((state) => state.session.sessionId);
  const prevSessionIdRef = useRef(activeSessionId);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPreviewFile, setSelectedPreviewFile] = useState(null);

  const openPreview = useCallback((file) => {
    if (!file) return;
    setSelectedPreviewFile(file);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setSelectedPreviewFile(null);
  }, []);

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isKnowledgeSearchActive, setIsKnowledgeSearchActive] =
    useState(false);

  const [uploadedDocNames, setUploadedDocNames] = useState([]);
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState({});
  const [kbGroups, setKbGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [chatResetToken, setChatResetToken] = useState(0);

  // Keep KB custom selections only on the main chat route (`/user/new`).
  // Keep them only when a chat session is active; otherwise reset (even on `/user/new`).
  useEffect(() => {
    const pathname = String(location.pathname || "");
    let hasPendingKbSelection = false;
    try {
      hasPendingKbSelection =
        sessionStorage.getItem("kb_keep_selection_for_next_session") === "1" ||
        Boolean(sessionStorage.getItem("kb_pending_selected_files"));
    } catch {}
    const keepKbSelections =
      pathname.startsWith("/user/new") &&
      (Boolean(activeSessionId) || hasPendingKbSelection);

    if (!keepKbSelections) {
      setSelectedGroups([]);
      setSelectedFiles([]);
    }
  }, [location.pathname, activeSessionId]);

  // Restore KB selections passed via sessionStorage (e.g. from Document History) once we land on
  // `/user/new` with an active session.
  useEffect(() => {
    const pathname = String(location.pathname || "");
    if (!pathname.startsWith("/user/new")) return;
    if (!activeSessionId) return;

    let pendingFiles = null;
    let pendingGroups = null;
    try {
      const rawFiles = sessionStorage.getItem("kb_pending_selected_files");
      const rawGroups = sessionStorage.getItem("kb_pending_selected_groups");
      pendingFiles = rawFiles ? JSON.parse(rawFiles) : null;
      pendingGroups = rawGroups ? JSON.parse(rawGroups) : null;
    } catch {
      pendingFiles = null;
      pendingGroups = null;
    }

    if (Array.isArray(pendingFiles) && pendingFiles.length > 0) {
      setSelectedFiles(pendingFiles);
    }
    if (Array.isArray(pendingGroups) && pendingGroups.length > 0) {
      setSelectedGroups(pendingGroups);
    }

    if (pendingFiles || pendingGroups) {
      try {
        sessionStorage.removeItem("kb_pending_selected_files");
        sessionStorage.removeItem("kb_pending_selected_groups");
      } catch {}
    }
  }, [location.pathname, activeSessionId]);

  // Reset selections when switching between existing chat sessions,
  // except when the next session was created from a KB-driven "New Chat" flow.
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    const next = activeSessionId;

    const isSessionCleared = Boolean(prev) && !next;
    const isSessionSwitch = Boolean(prev) && Boolean(next) && prev !== next;

    if (isSessionCleared) {
      setSelectedGroups([]);
      setSelectedFiles([]);
    } else if (isSessionSwitch) {
      let keepForNext = false;
      try {
        keepForNext =
          sessionStorage.getItem("kb_keep_selection_for_next_session") === "1";
      } catch {}

      if (keepForNext) {
        try {
          sessionStorage.removeItem("kb_keep_selection_for_next_session");
        } catch {}
      } else {
        setSelectedGroups([]);
        setSelectedFiles([]);
      }
    }

    prevSessionIdRef.current = next;
  }, [activeSessionId]);

  // If user selects specific KB groups/files anywhere in the app
  // (RagSourcesPill, Document History, KB dropdown Custom, etc.),
  // force-disable "Global" KB mode so Custom flow takes over.
  useEffect(() => {
    const hasCustomSelections =
      (Array.isArray(selectedGroups) && selectedGroups.length > 0) ||
      (Array.isArray(selectedFiles) && selectedFiles.length > 0);

    if (isKnowledgeSearchActive && hasCustomSelections) {
      setIsKnowledgeSearchActive(false);
      // Keep backend preference consistent so Global doesn't auto-restore on refresh.
      if (token) {
        api
          .put(
            `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
            { preferences: { chat: { knowledge_search_enabled: false } } },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .catch(() => {});
      }
    }
  }, [isKnowledgeSearchActive, selectedGroups, selectedFiles]);

  const [aiPreferences, setAiPreferences] = useState(() => {
    const stored = localStorage.getItem("aiPreferences");
    return stored ? JSON.parse(stored) : DEFAULT_AI_PREFERENCES;
  });

  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    const storedState = localStorage.getItem("sidebarExpanded");
    if (storedState !== null && window.innerWidth > 768) {
      return JSON.parse(storedState);
    }
    return window.innerWidth > 768;
  });

  // ================================
  // ✅ FETCH USER PREFERENCES (IMPORTANT)
  // ================================
  const fetchPreferences = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/user-profile/preferences`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data;



      if (data?.preferences) {
        const prefs = data.preferences;


        console.log("Fetched preferences****:", prefs);

        // 🔥 Sync toggle states
        setIsSearchActive(
          prefs.chat?.web_search_enabled ?? false
        );

        setIsKnowledgeSearchActive(
          prefs.chat?.knowledge_search_enabled ?? false
        );
      }

      // 🎨 Apply theme
      if (data?.theme_mode) {
        const isDark = data.theme_mode === "dark";
        localStorage.setItem("darkMode", isDark);
        document.documentElement.setAttribute(
          "data-theme",
          isDark ? "dark" : "light"
        );
      }

    } catch (err) {
      console.error("❌ Error fetching preferences:", err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPreferences();
    }
  }, [token]);

  // ================================
  // Sidebar Resize Logic
  // ================================
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSidebarExpanded(false);
      } else {
        const stored = localStorage.getItem("sidebarExpanded");
        setSidebarExpanded(stored ? JSON.parse(stored) : true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Persist AI preferences
  useEffect(() => {
    localStorage.setItem("aiPreferences", JSON.stringify(aiPreferences));
  }, [aiPreferences]);

  // Persist sidebar state on desktop only
  useEffect(() => {
    if (window.innerWidth > 768) {
      localStorage.setItem(
        "sidebarExpanded",
        JSON.stringify(sidebarExpanded)
      );
    }
  }, [sidebarExpanded]);

  const resetAiPreferences = () => {
    setAiPreferences(DEFAULT_AI_PREFERENCES);
  };

  const triggerChatReset = () => setChatResetToken((t) => t + 1);

  return (
    <div className="app">
      <Sidebar
        expanded={sidebarExpanded}
        toggleSidebar={() => setSidebarExpanded(!sidebarExpanded)}
      />

      <div
        className={`main-content-wrapper ${
          sidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className={`main-split ${previewOpen ? "main-split--preview" : ""}`}>
          <div className="main-split__left">
            <nav className="topbar-wrapper">
              <TopBar
                sidebarExpanded={sidebarExpanded}
                toggleSidebar={() => setSidebarExpanded(!sidebarExpanded)}
                isSearchActive={isSearchActive}
                setIsSearchActive={setIsSearchActive}
                isKnowledgeSearchActive={isKnowledgeSearchActive}
                setIsKnowledgeSearchActive={setIsKnowledgeSearchActive}
                kbGroups={kbGroups}
                setKbGroups={setKbGroups}
                selectedGroups={selectedGroups}
                setSelectedGroups={setSelectedGroups}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                triggerChatReset={triggerChatReset}
              />
            </nav>

            <main className="page-content custom-ak-scollbar">
              <Outlet
                context={{
                  isSearchActive,
                  setIsSearchActive,
                  isKnowledgeSearchActive,
                  setIsKnowledgeSearchActive,
                  uploadedDocNames,
                  setUploadedDocNames,
                  uploadedDocumentIds,
                  setUploadedDocumentIds,
                  kbGroups,
                  setKbGroups,
                  selectedGroups,
                  setSelectedGroups,
                  selectedFiles,
                  setSelectedFiles,
                  chatResetToken,
                  aiPreferences,
                  setAiPreferences,
                  resetAiPreferences,
                  DEFAULT_AI_PREFERENCES,
                  previewOpen,
                  selectedPreviewFile,
                  openPreview,
                  closePreview,
                }}
              />
            </main>

            <ProcessingQueue />
          </div>

          {previewOpen ? (
            <div className="main-split__right">
              <FilePreviewPanel file={selectedPreviewFile} onClose={closePreview} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MainLayout1;
